import { useId, type ReactNode } from 'react';

/**
 * A line of the settings dialog (docs/design/mockups/Ayarlar.dc.html): the
 * name and a short explanation on the left, the control on the right.
 */
export function SettingRow({
  label,
  hint,
  children,
  stacked = false,
  'data-testid': testId,
}: {
  label: ReactNode;
  hint?: ReactNode;
  /** the control goes under the text (a long form, a list) */
  stacked?: boolean;
  children: (ids: { id: string; labelId: string; hintId: string | undefined }) => ReactNode;
  'data-testid'?: string;
}) {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className={stacked ? 'setting-row setting-row--stacked' : 'setting-row'} data-testid={testId}>
      <div className="setting-row__text">
        <label id={labelId} htmlFor={id} className="setting-row__label">
          {label}
        </label>
        {hint ? (
          <span id={hintId} className="setting-row__hint">
            {hint}
          </span>
        ) : null}
      </div>
      <div className="setting-row__control">{children({ id, labelId, hintId })}</div>
    </div>
  );
}

/** A titled block inside a settings section (password, sessions, …). */
export function SettingGroup({ title, hint, children, 'data-testid': testId }: { title: ReactNode; hint?: ReactNode; children: ReactNode; 'data-testid'?: string }) {
  return (
    <section className="setting-group" data-testid={testId}>
      <h3 className="setting-group__title">{title}</h3>
      {hint ? <p className="setting-group__hint">{hint}</p> : null}
      {children}
    </section>
  );
}
